import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { RowCentered, RowForm, SubmitWrapper } from 'pages/Login/style';
import { UPDATE_USER_PROFILE_DATA } from 'redux/sagas/actions/authentication';
import { SET_ACCESS_ERROR } from 'redux/slices/authentication';
import { Button, DropDown, Input } from 'ui/atoms';
import phonePrefixes from '../../../utils/phonePrefixes';
import { PhoneSection } from './style';

const reducer = (state, action) => {
  switch (action.type) {
    case 'add_name':
      return {
        ...state,
        name: action.payload
      };
    case 'add_last_name':
      return {
        ...state,
        lastName: action.payload
      };
    case 'add_phone_number_prefix':
      return {
        ...state,
        prefix: action.payload
      };
    case 'add_phone_number_part':
      return {
        ...state,
        phoneNumber: action.payload
      };
    case 'add_company_name':
      return {
        ...state,
        companyCode: action.payload
      };
    default:
      return state;
  }
};

const newField = () => '';

const initialState = {
  name: newField(),
  lastName: newField(),
  phoneNumber: newField(),
  prefix: newField(),
  companyCode: newField()
};

const ManagePersonalData = ({ updateUserData, isOnLoginFlow, accountData }) => {
  const [phonePrefixesForDropdown, setPhonePrefixesForDropdown] = useState([]);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { t } = useTranslation();

  const compare = (a, b) => {
    if (a.value?.toString() < b.value?.toString()) {
      return -1;
    }
    if (a.value?.toString() > b.value?.toString()) {
      return 1;
    }
    // a must be equal to b
    return 0;
  };

  useEffect(() => {
    if (phonePrefixes) {
      setPhonePrefixesForDropdown(
        [...new Set(phonePrefixes.sort(compare).map(a => a.value))].map(a => ({
          value: a,
          label: a
        }))
      );
    }
  }, []);

  useEffect(() => {
    if (accountData) {
      dispatch({
        type: 'add_name',
        payload: accountData.name
      });
      dispatch({
        type: 'add_last_name',
        payload: accountData.surname
      });
      dispatch({
        type: 'add_phone_number_part',
        payload: accountData.phoneNumberPart
      });
      dispatch({
        type: 'add_phone_number_prefix',
        payload: accountData.phoneNumberPrefix
      });
      dispatch({
        type: 'add_company_name',
        payload: accountData.companyCode
      });
    }
  }, [accountData]);

  const isDisabled = () =>
    !state.name || !state.lastName || !state.prefix || !state.phoneNumber;

  const updateData = () => {
    updateUserData({ state, isOnLoginFlow });
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter' && !isDisabled()) {
      updateData();
    }
  };

  const findDefaultIndex = () => {
    return phonePrefixesForDropdown
      .findIndex(prefix => prefix.value === state.prefix)
      .toString();
  };

  return (
    <>
      <div onKeyPress={handleKeyPress}>
        <RowForm managePersonalData editingPersonalData>
          <Input
            type="text"
            placeholder={!isOnLoginFlow ? `${t('name')}` : `${t('name')}*`}
            variant={!isOnLoginFlow ? 'editaccountdata' : 'login'}
            value={state.name}
            onChange={e =>
              dispatch({
                type: 'add_name',
                payload: e.currentTarget.value
              })
            }
          />
        </RowForm>
        <RowForm managePersonalData editingPersonalData>
          <Input
            type="text"
            placeholder={
              !isOnLoginFlow ? `${t('surname')}` : `${t('surname')}*`
            }
            variant={!isOnLoginFlow ? 'editaccountdata' : 'login'}
            value={state.lastName}
            onChange={e =>
              dispatch({
                type: 'add_last_name',
                payload: e.currentTarget.value
              })
            }
          />
        </RowForm>
        <PhoneSection editingPersonalData isOnLoginFlow={isOnLoginFlow}>
          <DropDown
            defaultIndex={findDefaultIndex()}
            type="tel"
            options={phonePrefixesForDropdown}
            placeholder={!isOnLoginFlow ? `${t('prefix')}` : `${t('prefix')}*`}
            variant={!isOnLoginFlow ? 'editaccountdata' : 'login'}
            onChange={selection => {
              dispatch({
                type: 'add_phone_number_prefix',
                payload: selection
              });
            }}
            detachedOptions
            checkBoxMenu={false}
            isMulti={false}
          />
          <Input
            type="tel"
            placeholder={
              !isOnLoginFlow ? `${t('phone_number')}` : `${t('phone_number')}*`
            }
            variant={!isOnLoginFlow ? 'editaccountdata' : 'login'}
            value={state.phoneNumber}
            onChange={e =>
              dispatch({
                type: 'add_phone_number_part',
                payload: e.currentTarget.value
              })
            }
            phoneSection
          />
        </PhoneSection>
        {/*
        terms and conditions text: not deleted because it might be restored in the future
        isOnLoginFlow && (
          <RowForm smallerPaddingTop>
            <TCText>
              <MainFont>
                {t('tc_desc')} <strong>Terms & Conditions</strong>.
              </MainFont>
            </TCText>
          </RowForm>
        )*/}
      </div>
      <RowCentered>
        <SubmitWrapper>
          <Button
            variant={!isOnLoginFlow ? 'editaccountdata' : 'login'}
            disabled={isDisabled()}
            status={isDisabled()}
            onClick={() => {
              if (!isDisabled()) {
                updateData();
              }
            }}
          >
            {!isOnLoginFlow ? t('confirm') : t('submit')}
          </Button>
        </SubmitWrapper>
      </RowCentered>
    </>
  );
};

ManagePersonalData.propTypes = {
  genericError: PropTypes.string,
  updateUserData: PropTypes.func,
  isOnLoginFlow: PropTypes.bool
};

ManagePersonalData.defaultProps = {
  isOnLoginFlow: false
};

export default connect(
  ({ authentication }) => ({
    genericError: authentication.errorMessage
  }),
  dispatch => ({
    setError: payload => dispatch(SET_ACCESS_ERROR(payload)),
    updateUserData: payload =>
      dispatch({ type: UPDATE_USER_PROFILE_DATA._REQUEST, payload })
  })
)(ManagePersonalData);
